import React from 'react';
import { Form, message, Progress, Select, Table } from 'antd'; // eslint-disable-next-line
import { config, types, utils } from 'utils';
import { partyColors } from 'utils/party';
import _ from 'lodash';
import { PSDetails } from './PollingStationDetails';

interface Props {
    year: string;
    fetchType: 'api' | 'file';
}
const PollingStationDisplay = ({ year, fetchType }: Props) => {
    const [filterForm] = Form.useForm();

    const [data, setData] = React.useState<types.PollingData[]>([]);
    const [filters, setFilters] = React.useState({ region: '', constituency: '',psname:'' });
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        fetchData(); // eslint-disable-next-line
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            if (fetchType === 'file') {
                const response = await fetch(`data/polling/${year}_result.json`);
                let res: types.PollingData[] = await response.json();
                // console.log(JSON.stringify(res[0]));
                setData(res);
            } else {
                const response = await fetch(`${config.api.baseUrl}/results${year}`);
                let res: types.PollingData[] = await response.json();
                setData(res);
            }
            setLoading(false);
        } catch (error) {
            console.log(error);
            message.error(`Error loading data from ${fetchType}`);
        }
    }

    const columns = [
        {
            title: 'PS Code',
            dataIndex: 'pscode',
            key: 'pscode',
        },
        {
            title: 'PS Name',
            dataIndex: 'psname',
            key: 'psname',
        },
        {
            title: 'Presidential',
            dataIndex: 'prezwinner',
            key: 'prezwinner',
            render: (winner: string, row: types.PollingData) => {
                return (
                    <div className="font-semibold" style={{ color: `${partyColors[winner] || 'black'}` }}>
                        {winner} {(row.turnoutprez || 0).toFixed(2)}%
                        <Progress
                            showInfo={false}
                            size={["100%", 10]}
                            percent={row.turnoutprez}
                            strokeColor={partyColors[winner] || 'black'}
                        />
                    </div>
                )
            }
        },
        {
            title: 'Parliamentary',
            dataIndex: 'parlwinner',
            key: 'parlwinner',
            render: (winner: string, row: types.PollingData) => {
                return (
                    <div className="font-semibold" style={{ color: `${partyColors[winner] || 'black'}` }}>
                        {winner} {(row.turnoutparl || 0).toFixed(2)}%
                        <Progress
                            showInfo={false}
                            size={["100%", 10]}
                            percent={row.turnoutparl}
                            strokeColor={partyColors[winner] || 'black'}
                        />
                    </div>
                )
            }
        },
    ];

    const regions = _.uniq(data.map(row => row.region)).sort();
    const constituencies = _.uniq(data.filter(row => row.region.toLowerCase() === filters.region.toLowerCase()).map(row => row.constituency)).sort();
    const pollingStations = _.uniq(data.filter(row => row.constituency.toLowerCase() === filters.constituency.toLowerCase()).map(row => row.psname)).sort()
    const filteredData = data.filter(row => (
        (!filters.region || filters.region.toLowerCase() === row.region.toLowerCase()) &&
        (!filters.constituency || filters.constituency.toLowerCase() === row.constituency.toLowerCase()) &&
        (!filters.psname || filters.psname.toLowerCase() === row.psname.toLowerCase())
    ));

    return (
        <React.Fragment>
            <div className="border rounded-lg">
                <div className="text-center font-medium p-3 rounded-tr-lg rounded-tl-lg bg-black text-white">
                    Results for {year}
                </div>
                <Form
                    form={filterForm} layout="vertical" autoComplete="off"
                    onFieldsChange={e => {
                        const input = e[0];
                        setFilters(sf => ({
                            ...sf,
                            [input.name[0]]: input.value,
                            constituency: input.name[0] === 'region' ? '' : input.name[0] === 'constituency' ? input.value : sf.constituency,
                        }));
                        if (input.name[0] === 'region') {
                            filterForm.setFieldValue('constituency', '');
                        }
                    }}
                >
                    <div className="p-4 grid grid-cols-3 gap-6">
                        <Form.Item name="region" initialValue="" className="mb-0">
                            <Select placeholder="Select a Region" optionFilterProp="children" showSearch>
                                <Select.Option value="">All Regions</Select.Option>
                                {regions.map(option => (
                                    <Select.Option key={option} value={option}>{option}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="constituency" initialValue="" className="mb-0">
                            <Select placeholder="Select a Constituency" optionFilterProp="children" showSearch>
                                <Select.Option value="">All Constituencies</Select.Option>
                                {constituencies.map(option => (
                                    <Select.Option key={option} value={option}>{option}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="psname" initialValue="" className="mb-0">
                            <Select placeholder="Select a Polling Station" optionFilterProp="children" showSearch>
                                <Select.Option value="">All Polling Stations</Select.Option>
                                {pollingStations.map(option => (
                                    <Select.Option key={option} value={option}>{option}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>
                </Form>
                <div className="shadows p-4s rounded-br-lg rounded-bl-lg">
                    <Table
                        dataSource={filteredData} columns={columns} loading={loading}
                        onRow={(row) => ({
                            style: { cursor: 'pointer' },
                            onClick: () => {
                                const detailsWindow = window.open('', '_blank', 'popup=yes,width=870,height=730');
                                if (detailsWindow) {
                                    detailsWindow.document.write(PSDetails(year, row));
                                } else {
                                    console.error('Unable to open details window.');
                                }
                            }
                        })}
                    />
                </div>
            </div>
        </React.Fragment>
    );

}

export default PollingStationDisplay;